import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Plus, Pencil, Copy, Trash, PaperPlaneTilt, ArrowLeft } from '@phosphor-icons/react';
import { format } from 'date-fns';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';

function TemplateList() {
  const navigate = useNavigate();
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const businessId = selectedBusiness?.business?._id;

  const fetchTemplates = useCallback(async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/agreements/business/${businessId}/templates`);
      setTemplates(res.data || []);
    } catch {
      setTemplates([]);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (businessLoading) return;
    if (businessId) fetchTemplates();
  }, [businessLoading, businessId, fetchTemplates]);

  if (businessLoading || !businessId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate('/supplier/agreements')}
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Agreement templates
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Plus size={18} />}
          onClick={() => navigate('/supplier/agreements/templates/new')}
        >
          Create template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right" width={140} />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 1.5 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Plus size={18} />}
                      onClick={() => navigate('/supplier/agreements/templates/new')}
                      sx={{
                        py: 1.25,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        borderStyle: 'dashed',
                        borderColor: 'divider',
                        color: 'text.primary',
                        '& .MuiButton-startIcon': { mr: 1 },
                        '&:hover': {
                          borderColor: '#f06292',
                          bgcolor: '#fce4ec'
                        }
                      }}
                    >
                      Create template
                    </Button>
                  </TableCell>
                </TableRow>
                {templates.map((t) => (
                  <TableRow key={t._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t.templateName || 'Untitled template'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {t.updatedAt ? format(new Date(t.updatedAt), 'dd MMM yyyy') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/supplier/agreements/templates/${t._id}/edit`)}
                        aria-label="Edit template"
                      >
                        <Pencil size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/api/agreements/${t._id}`);
                            const data = res.data;
                            const { data: created } = await api.post('/api/agreements/template', {
                              businessId: data.business?._id || data.business,
                              templateName: (data.templateName || 'Copy') + ' (copy)',
                              message: '',
                              termsText: data.termsText,
                              lineItems: data.lineItems,
                              billingSchedule: data.billingSchedule,
                              currency: data.currency,
                              content: data.content
                            });
                            fetchTemplates();
                            navigate(`/supplier/agreements/templates/${created._id}/edit`);
                          } catch (err) {
                            setError(err.response?.data?.message || 'Failed to duplicate');
                          }
                        }}
                        aria-label="Duplicate"
                      >
                        <Copy size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (!window.confirm('Delete this template?')) return;
                          try {
                            await api.delete(`/api/agreements/${t._id}`);
                            fetchTemplates();
                          } catch (err) {
                            setError(err.response?.data?.message || 'Failed to delete');
                          }
                        }}
                        aria-label="Delete"
                        color="error"
                      >
                        <Trash size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 4 }}>
                      <Box textAlign="center">
                        <PaperPlaneTilt size={40} weight="light" style={{ opacity: 0.5, marginBottom: 10 }} />
                        <Typography variant="body2" color="text.secondary">
                          No templates yet
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default TemplateList;
